import React, { useEffect, useState, useRef } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Candidate, Branch, CandidateRole, CandidateDepartment, CandidateSource, SocialMediaPlatform, Interview } from '../types/hr.types';
import { useToast } from './ui/Toast';
import { Phone, ChevronLeft, ChevronRight, Clock, Calendar as CalendarIcon, User, ArrowRight, ArrowLeft, Upload, FileText, X } from 'lucide-react';
import { getMonthDays, getToday } from '../utils/dateUtils';
import { mockInterviews } from '../utils/mockData';
import { motion, AnimatePresence } from 'framer-motion';
interface SchedulePhoneScreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (candidate: Candidate) => void;
}
const ROLES: CandidateRole[] = ['Team Lead', 'Loan Officer', 'Loan Partner', 'Telemarketer', 'Processor', 'Processing Manager', 'Admin'];
const DEPARTMENTS: CandidateDepartment[] = ['Sales', 'Operations', 'Processing'];
const SOURCES: CandidateSource[] = ['LinkedIn', 'Indeed', 'Referral', 'Social Media'];
const SOCIAL_MEDIA_PLATFORMS: SocialMediaPlatform[] = ['Instagram', 'LinkedIn', 'Facebook', 'Twitter', 'TikTok', 'Other'];
const INTERVIEWERS = ['Kaylin Stepho', 'Zeinab Haidar'];
const REFERRAL_USERS = ['John Smith', 'Sarah Johnson', 'Mike Williams', 'Emily Davis', 'Chris Brown'];
export function SchedulePhoneScreenModal({
  isOpen,
  onClose,
  onCreate
}: SchedulePhoneScreenModalProps) {
  const {
    addToast
  } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [currentDate, setCurrentDate] = useState(getToday());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [calendarDays, setCalendarDays] = useState<(Date | null)[]>([]);
  const [dayInterviews, setDayInterviews] = useState<Interview[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '' as CandidateRole | '',
    department: 'Sales' as CandidateDepartment,
    branch: 'Troy' as Branch,
    interviewedBy: '',
    candidateSource: '' as CandidateSource | '',
    referredBy: '',
    socialMediaPlatform: '' as SocialMediaPlatform | '',
    notes: '',
    resumeFileName: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    setCalendarDays(getMonthDays(currentDate.getFullYear(), currentDate.getMonth()));
  }, [currentDate]);
  useEffect(() => {
    if (selectedDate) {
      setDayInterviews(mockInterviews.filter(i => {
        const d = new Date(i.dateTime);
        return d.getDate() === selectedDate.getDate() && d.getMonth() === selectedDate.getMonth() && d.getFullYear() === selectedDate.getFullYear();
      }).sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()));
    } else {
      setDayInterviews([]);
    }
  }, [selectedDate]);
  useEffect(() => {
    if (isOpen) {
      const t = getToday();
      setCurrentDate(t);
      setSelectedDate(t);
      setSelectedTime('');
      setStep(1);
      setIsLoading(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: '',
        department: 'Sales',
        branch: 'Troy',
        interviewedBy: '',
        candidateSource: '',
        referredBy: '',
        socialMediaPlatform: '',
        notes: '',
        resumeFileName: ''
      });
      setErrors({});
    }
  }, [isOpen]);
  const handleDateClick = (date: Date) => {
    const t = getToday();
    t.setHours(0, 0, 0, 0);
    if (date < t) return;
    setSelectedDate(date);
    setSelectedTime('');
  };
  const validateStep1 = () => {
    const e: Record<string, string> = {};
    if (!formData.firstName.trim()) e.firstName = 'First name is required';
    if (!formData.lastName.trim()) e.lastName = 'Last name is required';
    if (!formData.email.trim()) e.email = 'Email is required';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email format';
    if (!formData.phone.trim()) e.phone = 'Phone is required';
    if (!formData.role) e.role = 'Role is required';
    if (!formData.interviewedBy) e.interviewedBy = 'Interviewer is required';
    if (!formData.candidateSource) e.candidateSource = 'Source is required';
    if (formData.candidateSource === 'Referral' && !formData.referredBy) e.referredBy = 'Referrer is required';
    if (formData.candidateSource === 'Social Media' && !formData.socialMediaPlatform) e.socialMediaPlatform = 'Platform is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) return;
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 600));
    const [h, m] = selectedTime.split(':').map(Number);
    const dt = new Date(selectedDate);
    dt.setHours(h, m, 0, 0);
    const nc: Candidate = {
      id: `c_${Date.now()}`,
      ...formData,
      role: formData.role as CandidateRole,
      candidateSource: formData.candidateSource as CandidateSource,
      phoneInterviewDate: dt.toISOString(),
      status: 'Phone Screen',
      ...(formData.candidateSource === 'Social Media' && formData.socialMediaPlatform ? {
        socialMediaPlatform: formData.socialMediaPlatform as SocialMediaPlatform
      } : {})
    };
    onCreate(nc);
    addToast({
      type: 'success',
      title: 'Phone Screen Scheduled',
      message: `${formData.firstName} ${formData.lastName}'s phone screen has been scheduled for ${dt.toLocaleDateString()} at ${selectedTime}.`
    });
    setIsLoading(false);
    onClose();
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(p => ({
      ...p,
      [name]: value
    }));
    if (errors[name]) setErrors(p => {
      const n = {
        ...p
      };
      delete n[name];
      return n;
    });
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setFormData(p => ({
      ...p,
      resumeFileName: f.name
    }));
  };
  const handleRemoveFile = () => {
    setFormData(p => ({
      ...p,
      resumeFileName: ''
    }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const timeSlots: string[] = [];
  for (let h = 9; h <= 17; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    if (h !== 17) {
      timeSlots.push(`${h.toString().padStart(2, '0')}:15`);
      timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
      timeSlots.push(`${h.toString().padStart(2, '0')}:45`);
    }
  }
  const monthName = currentDate.toLocaleString('default', {
    month: 'long',
    year: 'numeric'
  });
  const today = getToday();
  today.setHours(0, 0, 0, 0);
  const inputClass = (error?: string) => `w-full px-3 py-2 border ${error ? 'border-red-300 focus:ring-red-200' : 'border-gray-300 focus:ring-orange-500'} rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 text-sm bg-white`;
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  return <Modal isOpen={isOpen} onClose={onClose} title={step === 1 ? 'Candidate Details' : 'Schedule Phone Screening'} maxWidth="lg" data-id="element-615">
      <AnimatePresence mode="wait" data-id="element-616">
        {step === 1 ? <motion.div key="step1" initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -20
      }} transition={{
        duration: 0.2
      }} data-id="element-617">
            <div className="space-y-6" data-id="element-618">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-619">
                <div data-id="element-620">
                  <label htmlFor="firstName" className={labelClass} data-id="element-621">
                    First Name
                  </label>
                  <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={inputClass(errors.firstName)} placeholder="e.g. Jane" data-id="element-622" />
                  {errors.firstName && <p className="text-xs text-red-600 mt-1" data-id="element-623">
                      {errors.firstName}
                    </p>}
                </div>
                <div data-id="element-624">
                  <label htmlFor="lastName" className={labelClass} data-id="element-625">
                    Last Name
                  </label>
                  <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={inputClass(errors.lastName)} placeholder="e.g. Doe" data-id="element-626" />
                  {errors.lastName && <p className="text-xs text-red-600 mt-1" data-id="element-627">
                      {errors.lastName}
                    </p>}
                </div>
                <div data-id="element-628">
                  <label htmlFor="email" className={labelClass} data-id="element-629">
                    Email Address
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={inputClass(errors.email)} placeholder="jane@example.com" data-id="element-630" />
                  {errors.email && <p className="text-xs text-red-600 mt-1" data-id="element-631">{errors.email}</p>}
                </div>
                <div data-id="element-632">
                  <label htmlFor="phone" className={labelClass} data-id="element-633">
                    Phone Number
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputClass(errors.phone)} placeholder="(555) 123-4567" data-id="element-634" />
                  {errors.phone && <p className="text-xs text-red-600 mt-1" data-id="element-635">{errors.phone}</p>}
                </div>
                <div data-id="element-636">
                  <label htmlFor="role" className={labelClass} data-id="element-637">
                    Role / Position
                  </label>
                  <select id="role" name="role" value={formData.role} onChange={handleChange} className={inputClass(errors.role)} data-id="element-638">
                    <option value="" data-id="element-639">Select Role</option>
                    {ROLES.map(r => <option key={r} value={r} data-id="element-640">
                        {r}
                      </option>)}
                  </select>
                  {errors.role && <p className="text-xs text-red-600 mt-1" data-id="element-641">{errors.role}</p>}
                </div>
                <div data-id="element-642">
                  <label htmlFor="department" className={labelClass} data-id="element-643">
                    Department
                  </label>
                  <select id="department" name="department" value={formData.department} onChange={handleChange} className={inputClass()} data-id="element-644">
                    {DEPARTMENTS.map(d => <option key={d} value={d} data-id="element-645">
                        {d}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-646">
                  <label htmlFor="interviewedBy" className={labelClass} data-id="element-647">
                    Interviewed By
                  </label>
                  <select id="interviewedBy" name="interviewedBy" value={formData.interviewedBy} onChange={handleChange} className={inputClass(errors.interviewedBy)} data-id="element-648">
                    <option value="" data-id="element-649">Select Interviewer</option>
                    {INTERVIEWERS.map(i => <option key={i} value={i} data-id="element-650">
                        {i}
                      </option>)}
                  </select>
                  {errors.interviewedBy && <p className="text-xs text-red-600 mt-1" data-id="element-651">
                      {errors.interviewedBy}
                    </p>}
                </div>
                <div data-id="element-652">
                  <label htmlFor="branch" className={labelClass} data-id="element-653">
                    Branch Location
                  </label>
                  <select id="branch" name="branch" value={formData.branch} onChange={handleChange} className={inputClass()} data-id="element-654">
                    <option value="Troy" data-id="element-655">Troy</option>
                    <option value="Arizona" data-id="element-656">Arizona</option>
                    <option value="Miami" data-id="element-657">Miami</option>
                  </select>
                </div>
                <div className="md:col-span-2" data-id="element-658">
                  <label htmlFor="candidateSource" className={labelClass} data-id="element-659">
                    Candidate Source
                  </label>
                  <select id="candidateSource" name="candidateSource" value={formData.candidateSource} onChange={handleChange} className={inputClass(errors.candidateSource)} data-id="element-660">
                    <option value="" data-id="element-661">Select Source</option>
                    {SOURCES.map(s => <option key={s} value={s} data-id="element-662">
                        {s}
                      </option>)}
                  </select>
                  {errors.candidateSource && <p className="text-xs text-red-600 mt-1" data-id="element-663">
                      {errors.candidateSource}
                    </p>}
                </div>
                <AnimatePresence data-id="element-664">
                  {formData.candidateSource === 'Referral' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="md:col-span-2 overflow-hidden" data-id="element-665">
                      <label htmlFor="referredBy" className={labelClass} data-id="element-666">
                        Referred By
                      </label>
                      <select id="referredBy" name="referredBy" value={formData.referredBy} onChange={handleChange} className={inputClass(errors.referredBy)} data-id="element-667">
                        <option value="" data-id="element-668">Select Referrer</option>
                        {REFERRAL_USERS.map(u => <option key={u} value={u} data-id="element-669">
                            {u}
                          </option>)}
                      </select>
                      {errors.referredBy && <p className="text-xs text-red-600 mt-1" data-id="element-670">
                          {errors.referredBy}
                        </p>}
                    </motion.div>}
                </AnimatePresence>
                <AnimatePresence data-id="element-671">
                  {formData.candidateSource === 'Social Media' && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="md:col-span-2 overflow-hidden" data-id="element-672">
                      <label htmlFor="socialMediaPlatform" className={labelClass} data-id="element-673">
                        Social Media Platform
                      </label>
                      <select id="socialMediaPlatform" name="socialMediaPlatform" value={formData.socialMediaPlatform} onChange={handleChange} className={inputClass(errors.socialMediaPlatform)} data-id="element-674">
                        <option value="" data-id="element-675">Select Platform</option>
                        {SOCIAL_MEDIA_PLATFORMS.map(p => <option key={p} value={p} data-id="element-676">
                            {p}
                          </option>)}
                      </select>
                      {errors.socialMediaPlatform && <p className="text-xs text-red-600 mt-1" data-id="element-677">
                          {errors.socialMediaPlatform}
                        </p>}
                    </motion.div>}
                </AnimatePresence>
              </div>
              <div data-id="element-678">
                <label htmlFor="notes" className={labelClass} data-id="element-679">
                  Notes
                </label>
                <textarea id="notes" name="notes" value={formData.notes} onChange={e => setFormData(p => ({
              ...p,
              notes: e.target.value
            }))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 text-sm bg-white resize-none" placeholder="Add any initial notes about this candidate..." data-id="element-680" />
              </div>
              <div data-id="element-681">
                <label className={labelClass} data-id="element-682">Resume / CV</label>
                <div className="mt-1 flex justify-center px-6 pt-4 pb-4 border-2 border-gray-300 border-dashed rounded-lg hover:border-orange-400 transition-colors bg-gray-50" data-id="element-683">
                  {formData.resumeFileName ? <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm" data-id="element-684">
                      <FileText className="h-6 w-6 text-orange-500" data-id="element-685" />
                      <div className="flex-1 min-w-0" data-id="element-686">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[180px]" data-id="element-687">
                          {formData.resumeFileName}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-688">Ready to upload</p>
                      </div>
                      <button type="button" onClick={handleRemoveFile} className="p-1 hover:bg-gray-100 rounded-full text-gray-400 hover:text-red-500 transition-colors" data-id="element-689">
                        <X className="h-4 w-4" data-id="element-690" />
                      </button>
                    </div> : <div className="space-y-1 text-center" data-id="element-691">
                      <Upload className="mx-auto h-10 w-10 text-gray-400" data-id="element-692" />
                      <div className="flex text-sm text-gray-600" data-id="element-693">
                        <label htmlFor="file-upload-step1" className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none" data-id="element-694">
                          <span data-id="element-695">Upload a file</span>
                          <input id="file-upload-step1" name="file-upload" type="file" ref={fileInputRef} className="sr-only" accept=".pdf,.doc,.docx" onChange={handleFileChange} data-id="element-696" />
                        </label>
                        <p className="pl-1" data-id="element-697">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-698">
                        PDF, DOC up to 10MB
                      </p>
                    </div>}
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100" data-id="element-699">
                <Button variant="ghost" onClick={onClose} data-id="element-700">
                  Cancel
                </Button>
                <Button variant="primary" onClick={() => {
              if (validateStep1()) setStep(2);
            }} rightIcon={<ArrowRight className="h-4 w-4" data-id="element-702" />} data-id="element-701">
                  Continue
                </Button>
              </div>
            </div>
          </motion.div> : <motion.div key="step2" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: 20
      }} transition={{
        duration: 0.2
      }} data-id="element-703">
            <div className="mb-6 p-4 bg-orange-50 rounded-xl border border-orange-100" data-id="element-704">
              <div className="flex items-center gap-3" data-id="element-705">
                <div className="h-10 w-10 bg-orange-100 rounded-lg flex items-center justify-center" data-id="element-706">
                  <Phone className="h-5 w-5 text-orange-600" data-id="element-707" />
                </div>
                <div className="flex-1" data-id="element-708">
                  <p className="text-sm font-medium text-gray-900" data-id="element-709">
                    {formData.firstName} {formData.lastName}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-710">
                    {formData.role} • {formData.department}
                  </p>
                </div>
                <button onClick={() => setStep(1)} className="text-sm text-orange-600 hover:text-orange-700 font-medium flex items-center gap-1" data-id="element-711">
                  <ArrowLeft className="h-3 w-3" data-id="element-712" /> Edit
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-713">
              <div className="space-y-4" data-id="element-714">
                <div className="flex items-center justify-between mb-2" data-id="element-715">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-716">
                    <CalendarIcon className="h-4 w-4 text-gray-500" data-id="element-717" /> Select
                    Date
                  </h3>
                  <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1" data-id="element-718">
                    <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))} className="p-1 hover:bg-white hover:shadow-sm rounded-md transition-all" data-id="element-719">
                      <ChevronLeft className="h-4 w-4 text-gray-600" data-id="element-720" />
                    </button>
                    <span className="text-sm font-medium w-28 text-center text-gray-700" data-id="element-721">
                      {monthName}
                    </span>
                    <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))} className="p-1 hover:bg-white hover:shadow-sm rounded-md transition-all" data-id="element-722">
                      <ChevronRight className="h-4 w-4 text-gray-600" data-id="element-723" />
                    </button>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm" data-id="element-724">
                  <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-725">
                    {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <div key={`${d}-${i}`} className="text-xs font-semibold text-gray-400 py-1" data-id="element-726">
                        {d}
                      </div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1" data-id="element-727">
                    {calendarDays.map((date, idx) => {
                  if (!date) return <div key={`empty-${idx}`} className="h-9" data-id="element-728" />;
                  const isSelected = selectedDate && date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
                  const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
                  const isPast = date < today;
                  const hasInterviews = mockInterviews.some(i => {
                    const d = new Date(i.dateTime);
                    return d.getDate() === date.getDate() && d.getMonth() === date.getMonth() && d.getFullYear() === date.getFullYear();
                  });
                  return <button key={idx} onClick={() => handleDateClick(date)} disabled={isPast} className={`h-9 flex flex-col items-center justify-center rounded-lg text-sm relative transition-all duration-200 ${isSelected ? 'bg-orange-600 text-white shadow-md' : isToday ? 'bg-orange-50 text-orange-700 font-bold border border-orange-200' : isPast ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-50 text-gray-700'}`} data-id="element-729">
                          <span data-id="element-730">{date.getDate()}</span>
                          {hasInterviews && !isSelected && !isPast && <div className="absolute bottom-1 h-1 w-1 rounded-full bg-orange-400" data-id="element-731" />}
                        </button>;
                })}
                  </div>
                </div>
              </div>
              <div className="flex flex-col h-full" data-id="element-732">
                <div className="flex-1 space-y-4" data-id="element-733">
                  <div data-id="element-734">
                    <h3 className="font-semibold text-gray-900 mb-1" data-id="element-735">
                      {selectedDate ? selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                  }) : 'Select a date'}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-736">
                      {dayInterviews.length} existing interview
                      {dayInterviews.length !== 1 ? 's' : ''} scheduled
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 max-h-[140px] overflow-y-auto" data-id="element-737">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-738">
                      Schedule for this day
                    </h4>
                    {dayInterviews.length > 0 ? <div className="space-y-2" data-id="element-739">
                        {dayInterviews.map(interview => {
                    const time = new Date(interview.dateTime).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    });
                    return <div key={interview.id} className="flex items-center gap-2 text-sm bg-white p-2 rounded-lg border border-gray-200" data-id="element-740">
                              <div className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap" data-id="element-741">
                                {time}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-742">
                                <p className="font-medium text-gray-900 truncate text-xs" data-id="element-743">
                                  {interview.candidateName}
                                </p>
                                <div className="flex items-center gap-1 text-xs text-gray-500" data-id="element-744">
                                  <User className="h-2.5 w-2.5" data-id="element-745" />
                                  <span data-id="element-746">{interview.interviewer}</span>
                                </div>
                              </div>
                            </div>;
                  })}
                      </div> : <div className="text-center py-4 text-gray-400 text-sm" data-id="element-747">
                        No interviews scheduled
                      </div>}
                  </div>
                  <div data-id="element-748">
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2" data-id="element-749">
                      <Clock className="h-4 w-4 text-gray-500" data-id="element-750" /> Select Time
                    </label>
                    <div className="grid grid-cols-4 gap-1.5 max-h-[160px] overflow-y-auto pr-1" data-id="element-751">
                      {timeSlots.map(time => <button key={time} onClick={() => setSelectedTime(time)} disabled={!selectedDate} className={`px-2 py-1.5 text-sm rounded-lg border transition-all duration-200 ${selectedTime === time ? 'bg-orange-600 text-white border-orange-600 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300 hover:bg-orange-50'} ${!selectedDate ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-752">
                          {time}
                        </button>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between pt-6 mt-6 border-t border-gray-100" data-id="element-753">
              <Button variant="ghost" onClick={() => setStep(1)} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-755" />} data-id="element-754">
                Back
              </Button>
              <div className="flex gap-3" data-id="element-756">
                <Button variant="ghost" onClick={onClose} disabled={isLoading} data-id="element-757">
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSubmit} isLoading={isLoading} disabled={!selectedDate || !selectedTime} leftIcon={<Phone className="h-4 w-4" data-id="element-759" />} data-id="element-758">
                  Schedule Phone Screen
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </Modal>;
}